import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ElementRef, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';

// Angular Material Datepicker
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, MAT_DATE_FORMATS, DateAdapter, MAT_DATE_LOCALE, NativeDateAdapter } from '@angular/material/core';

// Services
import { ToastService } from 'angular-toastify';
import { CookieService } from 'ngx-cookie-service';

// Models
import {
    CityParkingCustomerInfoPayload,
    CityParkingCustomerInfoResponse,
    CityParkingCustomerProfileResponse,
    CityParkingCustomerLinesResponse,
    CityParkingCustomerAddressPayload
} from '../../../../../core/models/customer.model';

interface Province { id: number; name_th: string; name_en: string; geography_id: number; }
interface Amphure { id: number; name_th: string; name_en: string; province_id: number; }
interface Tambon { id: number; zip_code: number; name_th: string; name_en: string; amphure_id: number; }

// Components
import { ModalCustomerAddComponent } from '../modal-customer-add/modal-customer-add.component';
import { ModalFullltaxFormComponent } from "../../other/modal-fullltax-form/modal-fullltax-form.component";
import { ApiCustomerManagementService } from '../../../../../core/services/api/customer/api-customer-management.service';
import { ModalAdminActivityHistoryComponent } from '../../system-settings/modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';
import { SiteStateService } from '../../../../../core/services/site-state.service';
import { ApiService } from '../../../../../core/services/api.service';
import { ClientIInfo } from '../../../../../core/models/parking.model';

// Custom Date Format สำหรับแสดงผล วว/ดด/ปป (DD/MM/YYYY)
export const CUSTOM_DATE_FORMATS = {
    parse: {
        dateInput: 'DD/MM/YYYY',
    },
    display: {
        dateInput: { day: '2-digit', month: '2-digit', year: 'numeric' } as Intl.DateTimeFormatOptions,
        monthYearLabel: { year: 'numeric', month: 'short' } as Intl.DateTimeFormatOptions,
        dateA11yLabel: { year: 'numeric', month: 'long', day: 'numeric' } as Intl.DateTimeFormatOptions,
        monthYearA11yLabel: { year: 'numeric', month: 'long' } as Intl.DateTimeFormatOptions,
    }
};


@Component({
    selector: 'app-modal-customer',
    standalone: true,
    imports: [CommonModule, FormsModule, ModalCustomerAddComponent, ModalFullltaxFormComponent, MatDatepickerModule, MatNativeDateModule, ModalAdminActivityHistoryComponent],
    templateUrl: './modal-customer.component.html',
    styleUrls: ['./modal-customer.component.css'],
    providers: [
        { provide: DateAdapter, useClass: NativeDateAdapter },
        { provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'th-TH' }
    ]
})
export class ModalCustomerComponent implements OnInit {

    @Input() customer: CityParkingCustomerInfoResponse | null = null;
    @Output() close = new EventEmitter<boolean>();
    @Output() save = new EventEmitter<void>();

    readonly activityEntityTypes = AdminActivityEntityTypes;
    showActivityHistory = false;

    // --- UI State ---
    activeTab: 'info' | 'address' | 'line' = 'info';
    isLoading: boolean = false;
    isAddModel: boolean = false;
    isCloseModal: boolean = false;

    // --- Inline Edit / Validation ---
    editingField: string | null = null;
    tempEditValue: any = null;
    validationError: string | null = null;
    hasInfoChanged: boolean = false;

    // --- Address State ---
    isAddressEmpty: boolean = true;
    isEditingAddress: boolean = false;
    isCreatingAddress: boolean = false;

    // --- Modal State ---
    isAddLineMemberModalOpen: boolean = false;
    isDeleteLineMemberModalOpen: boolean = false;
    itemToDelete: CityParkingCustomerLinesResponse | null = null;
    deleteType: 'line' | 'other' = 'line'; // Track what we are deleting

    // --- Data Models ---
    userId: number = 0;
    profileRaw: CityParkingCustomerProfileResponse | null = null;
    lineMembers: CityParkingCustomerLinesResponse[] = [];

    // Master Data (Location)
    tambons: Tambon[] = [];
    amphures: Amphure[] = [];
    provinces: Province[] = [];

    // Filtered Master Data
    filteredAmphures: Amphure[] = [];
    filteredTambons: Tambon[] = [];

    // Address Selection State (IDs)
    selectedProvinceId: number | null = null;
    selectedAmphureId: number | null = null;
    selectedTambonId: number | null = null;

    // Form Data: Info
    infoData: CityParkingCustomerInfoPayload = this.getInitialInfoData();

    readonly siteState = inject(SiteStateService);
    private readonly apiLegacy = inject(ApiService);
    customerClients: ClientIInfo[] = [];

    // Form Data: Address
    addressData: CityParkingCustomerAddressPayload = this.getInitialAddressData();

    constructor(
        private apiCustomerManagementService: ApiCustomerManagementService,
        private toast: ToastService,
        private cookieService: CookieService,
        private http: HttpClient,
        private el: ElementRef
    ) { }

    ngOnInit() {
        this.initializeUserId();
        this.loadMasterData(); // โหลดข้อมูลที่อยู่ก่อน

        if (this.customer) {
            this.isAddModel = false;
            this.getCityParkingCustomerProfileById(this.customer.id);
        } else {
            this.isAddModel = true;
            const siteId = this.siteState.selectedSite()?.id ?? 0;
            if (siteId) {
                this.infoData.siteId = siteId;
                this.infoData.siteName = this.siteState.selectedSite()?.name ?? '';
                this.loadCustomerClients(siteId);
            }
        }
    }

    /* ---------------------- Initialization ---------------------- */

    private initializeUserId() {
        const userIdFromSession = sessionStorage.getItem('user_id');
        const userIdFromCookie = this.cookieService.get('user_id');
        this.userId = userIdFromCookie ? parseInt(userIdFromCookie) : (userIdFromSession ? JSON.parse(userIdFromSession) : 0);
    }

    private loadMasterData() {
        // ไม่จำเป็นต้องรอ 1 วินาที ยิงขอข้อมูลเลย
        forkJoin({
            tambons: this.http.get<any[]>('json/thai_tambons.json'),
            amphures: this.http.get<any[]>('json/thai_amphures.json'),
            provinces: this.http.get<any[]>('json/thai_provinces.json'),
        }).subscribe({
            next: (response) => {
                this.tambons = response.tambons;
                this.amphures = response.amphures;
                this.provinces = response.provinces as Province[];
                this.startEditAddressFilters();
            },
            error: (err) => console.error('Failed to load location master data', err)
        });
    }

    private getInitialInfoData(): CityParkingCustomerInfoPayload {
        return {
            id: 0, name: '', enable: true, phone: '', email: '', remark: '',
            siteId: undefined, clientId: undefined, siteName: '', clientName: '',
            startDate: undefined, endDate: undefined, userId: 0
        };
    }

    private loadCustomerClients(siteId: number): void {
        if (!siteId) {
            this.customerClients = [];
            return;
        }
        this.isLoadingCustomerClients = true;
        this.apiLegacy.GetClientIInfo(siteId).subscribe({
            next: (res) => {
                this.customerClients = [...(res ?? [])].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', 'th'));
                const selected = this.customerClients.find(c => c.id === this.infoData.clientId);
                if (selected) this.infoData.clientName = selected.name ?? '';
                this.clientSearchText = this.infoData.clientName ?? '';
                this.isLoadingCustomerClients = false;
            },
            error: () => {
                this.customerClients = [];
                this.isLoadingCustomerClients = false;
            },
        });
    }

    private validateSiteAndClient(): boolean {
        if (!this.infoData.siteId) {
            this.toast.error('กรุณาเลือกลานจากแถบด้านข้างก่อน');
            return false;
        }
        if (!this.infoData.clientId) {
            this.toast.error('กรุณาเลือกบริษัทผู้เช่า');
            return false;
        }
        return true;
    }

    private getInitialAddressData(): CityParkingCustomerAddressPayload {
        return {
            id: 0, name: '', fullName: '', address: '', subDistrict: '', district: '',
            province: '', zipCode: '', headOffice: true, taxId: '', branchNo: '',
            branchName: '', phone: '', email: '', type: 0, cpCustomerId: 0, userId: 0
        };
    }

    /* ---------------------- Helper Functions ---------------------- */

    // แปลง Date หรือ ISO string ให้เป็น Date object สำหรับ MatDatepicker
    private formatDateForInput(date: any): Date | undefined {
        if (!date) return undefined;
        const d = new Date(date);
        if (isNaN(d.getTime())) return undefined;
        return d;
    }

    // ================= Normal Functions =================

    /* ---------------------- UI Actions ---------------------- */

    /**
     * Toggles the customer enable status and immediately saves it to the backend via API
     * สลับสถานะการเปิดใช้งานของลูกค้าและส่งข้อมูลบันทึกลง API ทันที
     * @param event The change event from the toggle switch input
     */
    toggleEnableStatus(event: any) {
        if (this.isAddModel) {
            this.infoData.enable = event.target.checked;
            return;
        }

        const newStatus = event.target.checked;
        this.infoData.enable = newStatus;

        this.sentApiUpdateCustomer();
    }

    closeModal() {
        this.close.emit(true);
    }

    switchTab(tab: 'info' | 'address' | 'line') {
        this.activeTab = tab;
        this.cancelEdit();
    }

    /* ---------------------- Address Logic ---------------------- */

    onProvinceChange() {
        // Reset children
        this.selectedAmphureId = null;
        this.selectedTambonId = null;
        this.filteredAmphures = [];
        this.filteredTambons = [];

        // Clear Address Data for children
        this.addressData.district = '';
        this.addressData.subDistrict = '';
        this.addressData.zipCode = '';

        if (this.selectedProvinceId) {
            const province = this.provinces.find(p => p.id == this.selectedProvinceId);
            if (province) {
                this.addressData.province = province.name_th;
                this.filteredAmphures = this.amphures.filter(a => a.province_id == this.selectedProvinceId);
            }
        } else {
            this.addressData.province = '';
        }
    }

    onAmphureChange() {
        // Reset children
        this.selectedTambonId = null;
        this.filteredTambons = [];

        // Clear Address Data for children
        this.addressData.subDistrict = '';
        this.addressData.zipCode = '';

        if (this.selectedAmphureId) {
            const amphure = this.amphures.find(a => a.id == this.selectedAmphureId);
            if (amphure) {
                this.addressData.district = amphure.name_th;
                this.filteredTambons = this.tambons.filter(t => t.amphure_id == this.selectedAmphureId);
            }
        } else {
            this.addressData.district = '';
        }
    }

    onTambonChange() {
        if (this.selectedTambonId) {
            const tambon = this.tambons.find(t => t.id == this.selectedTambonId);
            if (tambon) {
                this.addressData.subDistrict = tambon.name_th;
                this.addressData.zipCode = String(tambon.zip_code);
            }
        } else {
            this.addressData.subDistrict = '';
            this.addressData.zipCode = '';
        }
    }

    startAddAddress() {
        this.isCreatingAddress = true;
        this.addressData = this.getInitialAddressData();
        this.addressData.cpCustomerId = this.infoData.id;
        this.addressData.userId = this.userId;
    }



    closeFulltaxModal() {
        this.isCreatingAddress = false;
    }
    saveFulltaxModal(event: any) {
        this.isCreatingAddress = false;
        this.getCityParkingCustomerProfileById(this.infoData.id);
    }
    /* ---------------------- Inline Edit Logic ---------------------- */

    startEdit(field: string, value: any) {
        this.editingField = field;
        this.tempEditValue = value; // Deep copy if object
        this.validationError = null;
    }

    cancelEdit() {
        this.editingField = null;
        this.tempEditValue = null;
        this.validationError = null;
        this.isEditingAddress = false;
        this.isCreatingAddress = false;
    }

    saveEdit(field: string) {
        if (!this.validateInput(field, this.tempEditValue)) return;

        // --- INFO TAB ---
        if (field in this.infoData || field === 'startDate' || field === 'endDate') {
            (this.infoData as any)[field] = this.tempEditValue;

            // mark ว่ามีการเปลี่ยน
            this.hasInfoChanged = true;

            this.cancelEdit();
            return;
        }

        // --- ADDRESS (ยังยิง API ได้เหมือนเดิม) ---
        if (field in this.addressData || field === 'address') {
            (this.addressData as any)[field] = this.tempEditValue;

            if (this.prepareAndValidateAddressPayload()) {
                if (field === 'headOffice' && this.tempEditValue === true) {
                    this.addressData.branchNo = '';
                    this.addressData.branchName = '';
                }
                this.sentApiUpdateAddress();
            }
            this.cancelEdit();
        }
    }

    saveAddress() {
        if (this.prepareAndValidateAddressPayload()) {
            if (this.addressData.headOffice) {
                this.addressData.branchNo = '';
                this.addressData.branchName = '';
            }
            this.sentApiUpdateAddress();
            this.cancelEdit();
        }
    }

    saveInfoTab() {
        if (!this.validateInput('name', this.infoData.name)) {
            return this.toast.error(this.validationError!);
        }

        if (!this.validateInput('phone', this.infoData.phone)) {
            return this.toast.error(this.validationError!);
        }

        if (this.infoData.email && !this.validateInput('email', this.infoData.email)) {
            return this.toast.error(this.validationError!);
        }

        if (!this.validateDates('startDate', this.infoData.startDate)) {
            return this.toast.error(this.validationError!);
        }

        if (!this.validateSiteAndClient()) {
            return;
        }

        this.infoData.userId = this.userId;

        // console.log('FINAL INFO PAYLOAD:', this.infoData);

        this.sentApiUpdateCustomer();
        this.hasInfoChanged = false;
    }


    /* ---------------------- Validation ---------------------- */

    validateInput(field: string, value: any): boolean {
        // Common Patterns
        const numberOnly = /^[0-9]+$/;
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const zipPattern = /^[0-9]{5}$/;

        switch (field) {
            case 'name':
                if (!value?.trim()) return this.setError('กรุณากรอกชื่อลูกค้า');
                break;
            case 'phone':
                if (!value?.trim()) return this.setError('กรุณากรอกเบอร์โทรศัพท์');
                if (!numberOnly.test(value)) return this.setError('เบอร์โทรศัพท์ต้องเป็นตัวเลขเท่านั้น');
                break;
            case 'email':
                if (value && !emailPattern.test(value)) return this.setError('รูปแบบอีเมลไม่ถูกต้อง');
                break;
            case 'zipCode':
                if (value && !zipPattern.test(value)) return this.setError('รหัสไปรษณีย์ต้องเป็นตัวเลข 5 หลัก');
                break;
            case 'startDate':
            case 'endDate':
                return this.validateDates(field, value);
        }
        return true;
    }

    private validateDates(field: string, value: any): boolean {
        const start = field === 'startDate' ? value : this.infoData.startDate;
        const end = field === 'endDate' ? value : this.infoData.endDate;
        if (start && end && new Date(start) > new Date(end)) {
            return this.setError('วันที่เริ่มต้องไม่มากกว่าวันที่สิ้นสุด');
        }
        return true;
    }

    // Unified Validation & Preparation
    private prepareAndValidateAddressPayload(): boolean {
        const data = this.addressData;

        // 1. Prepare Data (Defaults)
        // If name is empty, try to fallback to meaningful name (optional requirement, but good practice if API requires it)
        if (!data.name || !data.name.trim()) {
            data.name = data.headOffice ? 'สำนักงานใหญ่' : (data.branchName || 'ที่อยู่จัดส่ง');
        }

        // 2. Validate
        // Skip checking 'name' as requested by user, relying on auto-fill or API accepting it.

        if (!data.fullName) { this.toast.error('กรุณาระบุชื่อผู้รับ'); return false; }
        if (!data.address) { this.toast.error('กรุณาระบุที่อยู่'); return false; }
        if (!data.subDistrict || !data.district || !data.province) { this.toast.error('กรุณาระบุข้อมูลพื้นที่ให้ครบถ้วน'); return false; }
        if (!data.zipCode || !/^[0-9]{5}$/.test(data.zipCode)) { this.toast.error('รหัสไปรษณีย์ไม่ถูกต้อง'); return false; }

        // New Fields Validation
        if (!data.phone) { this.toast.error('กรุณาระบุเบอร์โทรศัพท์สำหรับติดต่อ'); return false; }
        // Email is optional usually, but if provided check format
        if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
            this.toast.error('รูปแบบอีเมลไม่ถูกต้อง');
            return false;
        }
        if (data.type === undefined || data.type === null) {
            this.toast.error('กรุณาระบุประเภทที่อยู่ (บุคคลธรรมดา/นิติบุคคล)');
            return false;
        }

        return true;
    }

    // OLD validateAddressData - Keep for reference or remove if fully replaced.
    // Allowing backward compat if other methods call it, but verify logic.
    // private validateAddressData(data: CityParkingCustomerAddressPayload): boolean { ... }

    private setError(msg: string): boolean {
        this.validationError = msg;
        return false;
    }

    /* ---------------------- Main API Operations ---------------------- */

    // Save New Customer (Info Tab)
    saveNewCustomer() {
        if (!this.validateInput('name', this.infoData.name)) return this.toast.error(this.validationError!);
        if (!this.validateInput('phone', this.infoData.phone)) return this.toast.error(this.validationError!);
        if (!this.infoData.startDate || !this.infoData.endDate) return this.toast.error('กรุณาระบุช่วงเวลา');
        if (!this.validateSiteAndClient()) return;

        this.infoData.userId = this.userId;
        //console.log("Info Save Data : ", this.infoData);
        this.sentApiUpdateCustomer();
    }
    // Helper to re-run filters when loading existing address
    private startEditAddressFilters() {
        if (!this.addressData) return;

        // Map Province Name -> ID
        if (this.addressData.province) {
            const p = this.provinces.find(x => x.name_th === this.addressData.province);
            if (p) {
                this.selectedProvinceId = p.id;
                this.filteredAmphures = this.amphures.filter(a => a.province_id === p.id);
            }
        }

        // Map Amphure Name -> ID
        if (this.addressData.district) {
            const a = this.filteredAmphures.find(x => x.name_th === this.addressData.district);
            if (a) {
                this.selectedAmphureId = a.id;
                this.filteredTambons = this.tambons.filter(t => t.amphure_id === a.id);
            }
        }

        // Map Tambon Name -> ID
        if (this.addressData.subDistrict) {
            const t = this.filteredTambons.find(x => x.name_th === this.addressData.subDistrict);
            if (t) {
                this.selectedTambonId = t.id;
            }
        }
    }

    // ... (ส่วนของ Line Member / Delete Modal เก็บไว้เหมือนเดิมได้ แต่จัดกลุ่มให้ดี) ...

    openAddLineMemberModal() { this.isAddLineMemberModalOpen = true; }

    closeAddLineMemberModal() {
        this.isAddLineMemberModalOpen = false;
    }

    saveAddLineMemberModal() {
        this.isAddLineMemberModalOpen = false;
        this.getCityParkingCustomerProfileById(this.infoData.id);
    }

    closeDeleteLineMemberModal() {
        this.isDeleteLineMemberModalOpen = false;
        this.itemToDelete = null;
    }

    saveDeleteLineMemberModal() {
        this.isDeleteLineMemberModalOpen = false;
        this.itemToDelete = null;
        this.getCityParkingCustomerProfileById(this.infoData.id);
    }

    onDeleteItem(member: CityParkingCustomerLinesResponse) {
        this.itemToDelete = member;
        this.isDeleteLineMemberModalOpen = true;
    }

    /* ---------------------- Drag & Drop ---------------------- */
    onDragStart(event: DragEvent, member: CityParkingCustomerLinesResponse) {
        if (event.dataTransfer) {
            const dragData = {
                type: 'LINE_MEMBER_SEARCH',
                phone: member.phone,
                name: member.fullName
            };
            event.dataTransfer.setData('application/json', JSON.stringify(dragData));
            event.dataTransfer.effectAllowed = 'copy';
        }

        // Hide the modal temporarily so user can see sidebar
        setTimeout(() => {
            if (this.el && this.el.nativeElement) {
                // Find the main modal container to hide
                const modalContainer = this.el.nativeElement.querySelector('.fixed.inset-0.z-50');
                if (modalContainer) {
                    modalContainer.style.opacity = '0';
                    modalContainer.style.pointerEvents = 'none';
                }
            }
        }, 50);
    }

    onDragEnd(event: DragEvent) {
        // Restore visibility
        if (this.el && this.el.nativeElement) {
            const modalContainer = this.el.nativeElement.querySelector('.fixed.inset-0.z-50');
            if (modalContainer) {
                modalContainer.style.opacity = '1';
                modalContainer.style.pointerEvents = 'auto';
            }
        }
    }

    /* ---------------------- Line Add Modal ---------------------- */
    openAddLineModal() {
        this.isAddLineMemberModalOpen = true;
    }

    closeAddLineModal() {
        this.isAddLineMemberModalOpen = false;
    }
    /*  ------------------------------------------------------------------------ API ------------------------------------------------------------------------ */
    getCityParkingCustomerProfileById(customerId: number) {
        this.isLoading = true;
        this.apiCustomerManagementService.getCityParkingCustomerProfileById(customerId)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (res) => {
                    console.log("Get Customer Profile Response: ", res);
                    this.profileRaw = res;
                    this.infoData = {
                        ...this.customer!,
                        userId: this.userId,
                        siteId: this.customer!.siteId || this.siteState.selectedSite()?.id,
                        clientId: this.customer!.clientId,
                        siteName: this.customer!.siteName || (this.siteState.selectedSite()?.name ?? ''),
                        clientName: this.customer!.clientName ?? '',
                        startDate: this.formatDateForInput(this.customer!.startDate) as any,
                        endDate: this.formatDateForInput(this.customer!.endDate) as any
                    };
                    if (this.infoData.siteId) {
                        this.loadCustomerClients(this.infoData.siteId);
                    }
                    this.lineMembers = res.cityParkingCustomerLinesResponses || [];
                    if (res.cityParkingAddressLists?.cpCustomerId !== null && res.cityParkingAddressLists?.cpCustomerId !== 0) {
                        this.addressData = { ...res.cityParkingAddressLists, userId: this.userId };
                        this.isAddressEmpty = false;
                        // Trigger filters for address dropdowns
                        this.startEditAddressFilters();
                    } else {
                        this.isAddressEmpty = true;
                    }

                },
                error: () => this.toast.error('ไม่สามารถโหลดข้อมูลลูกค้าได้')
            });
    }

    private formatDateForApi(value: any): string {
        if (!value) return '';
        if (typeof value === 'string' && value.length === 10 && value.includes('-')) {
            return value;
        }
        const d = value instanceof Date ? value : new Date(value);
        if (isNaN(d.getTime())) return '';
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }

    sentApiUpdateCustomer() {
        this.isLoading = true;
        const payload: CityParkingCustomerInfoPayload = {
            ...this.infoData,
            startDate: this.formatDateForApi(this.infoData.startDate) as any,
            endDate: this.formatDateForApi(this.infoData.endDate) as any
        };
        this.apiCustomerManagementService.updateCityParkingCustomerInfo(payload)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (res) => {
                    // console.log("Update Info Response: ", res);
                    if (res.isSuccess) {
                        this.toast.success(res.message);
                        this.save.emit();
                        if (this.isAddModel) this.closeModal();
                    } else {
                        this.toast.error(res.message);
                    }
                },
                error: () => this.toast.error('เกิดข้อผิดพลาดในการบันทึก')
            });
    }

    sentApiUpdateAddress() {
        this.isLoading = true;
        this.apiCustomerManagementService.updateCityparkingAddressCustomer(this.addressData)
            .pipe(finalize(() => this.isLoading = false))
            .subscribe({
                next: (res) => {
                    //console.log("Update Address Response: ", res);
                    if (res.isSuccess) {
                        this.toast.success(res.message);
                        this.getCityParkingCustomerProfileById(this.infoData.id);
                    } else {
                        this.toast.error(res.message);
                    }
                },
                error: () => this.toast.error('เกิดข้อผิดพลาดในการอัปเดตที่อยู่')
            });
    }

    saveNewAddress() {
        if (this.prepareAndValidateAddressPayload()) {
            this.isLoading = true;
            this.apiCustomerManagementService.updateCityparkingAddressCustomer(this.addressData)
                .pipe(finalize(() => this.isLoading = false))
                .subscribe({
                    next: (res) => {
                        if (res.isSuccess) {
                            this.toast.success('เพิ่มที่อยู่เรียบร้อย');
                            this.isCreatingAddress = false;
                            this.isAddressEmpty = false;
                            this.getCityParkingCustomerProfileById(this.infoData.id);
                        } else {
                            this.toast.error(res.message);
                        }
                    },
                    error: (err) => this.toast.error('เกิดข้อผิดพลาดในการบันทึกที่อยู่')
                });
        }
    }



    confirmDelete() {
        if (!this.itemToDelete) return;

        if (this.deleteType === 'line') {
            this.apiCustomerManagementService.DeleteLineMember(this.itemToDelete.id, this.customer?.id || 0)
                .subscribe({
                    next: (res) => {
                        console.log("Delete Line Member Response: ", res);
                        if (res.isSuccess) {
                            this.toast.success('ลบเรียบร้อย');
                            this.getCityParkingCustomerProfileById(this.customer?.id || 0);
                        } else {
                            this.toast.error(res.message);
                        }
                        this.closeDeleteModal();
                    },
                    error: () => {
                        this.toast.error('เกิดข้อผิดพลาด');
                        this.closeDeleteModal();
                    }
                });
        }
        // Handle other delete types if any in future
    }

    closeDeleteModal() { this.isDeleteLineMemberModalOpen = false; this.itemToDelete = null; }

    get canShowActivityHistory(): boolean {
        return !!this.customer?.id && this.customer.id > 0 && !this.isAddModel;
    }

    openActivityHistory(): void {
        if (!this.canShowActivityHistory) return;
        this.showActivityHistory = true;
    }

    closeActivityHistory(): void {
        this.showActivityHistory = false;
    }

    clientSearchText = '';
    isClientDropdownOpen = false;
    isLoadingCustomerClients = false;

    get filteredCustomerClients(): ClientIInfo[] {
        const term = this.clientSearchText.trim().toLowerCase();
        const selectedName = (this.infoData.clientName ?? '').trim().toLowerCase();
        const list = !term || term === selectedName
            ? this.customerClients
            : this.customerClients.filter(c => (c.name ?? '').toLowerCase().includes(term));
        return list.slice(0, 50);
    }

    openClientDropdown(): void {
        if (!this.infoData.siteId || !this.customerClients.length) return;
        this.isClientDropdownOpen = true;
    }

    selectCustomerClient(client: ClientIInfo): void {
        this.infoData.clientId = client.id;
        this.infoData.clientName = client.name ?? '';
        this.clientSearchText = this.infoData.clientName;
        this.isClientDropdownOpen = false;
    }

    onClientInputBlur(): void {
        this.isClientDropdownOpen = false;
        this.clientSearchText = this.infoData.clientName ?? '';
    }
}